import { getSession, signIn } from 'next-auth/react'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

type ClientSession = Awaited<ReturnType<typeof getSession>>

/** Reuse a session for this long, and never closer than the margin to token expiry. */
const SESSION_REUSE_MS = 30_000
const SESSION_EXPIRY_MARGIN_MS = 75_000

let cachedSession: { session: ClientSession; fetchedAt: number } | null = null
let sessionRequest: Promise<ClientSession> | null = null

function sessionExpiry(session: ClientSession): number | null {
  const value = (session as { accessTokenExpires?: unknown } | null)?.accessTokenExpires
  return typeof value === 'number' ? value : null
}

/**
 * One `/api/auth/session` round trip serves every request fired together
 * (ported from AP, R07 P2). Without this, a page that loads ten endpoints asked
 * the Next.js server for the session ten times; those calls queued, and each
 * could refresh the token with Keycloak, delaying every request by seconds.
 * A session is reused for 30 s, never within 75 s of its token expiry, and
 * `forceFresh` skips the cache (used once after a 401).
 */
export async function currentSession(forceFresh = false): Promise<ClientSession> {
  const now = Date.now()
  if (!forceFresh && cachedSession) {
    const expires = sessionExpiry(cachedSession.session)
    const fresh = now - cachedSession.fetchedAt < SESSION_REUSE_MS
    const notExpiring = expires === null || expires - now > SESSION_EXPIRY_MARGIN_MS
    if (fresh && notExpiring) return cachedSession.session
  }
  if (!sessionRequest) {
    sessionRequest = getSession()
      .then((session) => {
        cachedSession = { session, fetchedAt: Date.now() }
        return session
      })
      .finally(() => {
        sessionRequest = null
      })
  }
  return sessionRequest
}

/**
 * An API error that keeps the HTTP status and parsed body, so a caller can
 * tell a 404, 403 and 409 (stale version) apart. It is still an `Error` with
 * the server's `detail` as the message, so callers that only read `message`
 * are unaffected.
 */
export class ApiError extends Error {
  readonly status: number
  /** The parsed error body, for callers that need more than `detail` (for example a 409 conflict). */
  readonly body: unknown

  constructor(message: string, status: number, body: unknown = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/** Status of an error thrown by the API client, or null for a network or other error. */
export function apiErrorStatus(err: unknown): number | null {
  return err instanceof ApiError ? err.status : null
}

// Data state hook point (plan 08 N2). The backend may add `X-Data-Generation`
// to every `/api` response and answer writes with 503 + `Retry-After` while a
// baseline reset runs. The shell's DataStateWatcher subscribes here; until the
// backend sends the header, nothing is emitted.

export const DATA_GENERATION_HEADER = 'X-Data-Generation'
export const MAINTENANCE_MESSAGE = 'Data is being refreshed. Try again in a minute.'

export type DataStateEvent =
  | { type: 'generation-changed'; previous: string; current: string }
  | { type: 'maintenance'; message: string; retryAfterSeconds: number | null }

type DataStateListener = (event: DataStateEvent) => void

const dataStateListeners = new Set<DataStateListener>()
let lastGeneration: string | null = null

/** Listen for data state signals on API responses. Returns the unsubscribe function. */
export function subscribeDataState(listener: DataStateListener): () => void {
  dataStateListeners.add(listener)
  return () => {
    dataStateListeners.delete(listener)
  }
}

function emitDataState(event: DataStateEvent): void {
  for (const listener of dataStateListeners) {
    try {
      listener(event)
    } catch {
      // A broken listener must never fail the request that triggered it.
    }
  }
}

function observeResponse(response: Response): void {
  const generation = response.headers.get(DATA_GENERATION_HEADER)
  if (generation) {
    if (lastGeneration !== null && lastGeneration !== generation) {
      emitDataState({ type: 'generation-changed', previous: lastGeneration, current: generation })
    }
    lastGeneration = generation
  }
  const retryAfter = response.headers.get('Retry-After')
  if (response.status === 503 && retryAfter !== null) {
    const seconds = Number.parseInt(retryAfter, 10)
    emitDataState({
      type: 'maintenance',
      message: MAINTENANCE_MESSAGE,
      retryAfterSeconds: Number.isFinite(seconds) ? seconds : null,
    })
  }
}

/**
 * Coerce a FastAPI error body into a human-readable string. FastAPI returns
 * `detail` as a plain string for HTTPExceptions but as an array of
 * `{ loc, msg, type }` objects for 422 validation errors. Stringifying the
 * latter directly yields "[object Object]", which is what surfaced in the UI,
 * so flatten it to its `msg` text(s) here.
 */
function normalizeApiError(errorData: unknown): string {
  if (!errorData || typeof errorData !== 'object') {
    return typeof errorData === 'string' ? errorData : ''
  }
  const detail = (errorData as { detail?: unknown }).detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => {
        if (d && typeof d === 'object' && 'msg' in d) return String((d as { msg: unknown }).msg)
        return typeof d === 'string' ? d : ''
      })
      .filter(Boolean)
    if (msgs.length) return msgs.join('; ')
  }
  if (detail && typeof detail === 'object' && 'msg' in detail) {
    return String((detail as { msg: unknown }).msg)
  }
  // Fall back to a top-level `message` if present, else nothing.
  const message = (errorData as { message?: unknown }).message
  return typeof message === 'string' ? message : ''
}

/**
 * A robust API client that handles authentication and base path resolution.
 * @param endpoint The API endpoint to call, e.g., '/api/test' or '/api/admin/dashboard'.
 *                 The endpoint should include the '/api' prefix.
 * @param options Standard fetch options (method, body, etc.).
 */
async function apiClientFetch<T = unknown>(
  endpoint: string,
  options: RequestInit = {},
  retried = false,
): Promise<T> {
  const session = await currentSession(retried)

  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  // Handle session errors that occurred during a token refresh attempt.
  if (session?.error === 'RefreshAccessTokenError') {
    // Force sign-in to get a new, valid session.
    await signIn('keycloak')
    // Throw an error to stop the current, failing API call.
    throw new Error('Session expired. Please sign in again.')
  }

  // Construct the full URL: http://localhost:8001/app1/api/test
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const response = await fetch(fullUrl, { ...options, headers })
  observeResponse(response)

  // If the backend returns a 401, it means the token is invalid or expired.
  // This can happen if the backend is restarted or the user's session is revoked.
  // We trigger a re-login to fix the session.
  if (response.status === 401) {
    // The reused token may have been refreshed since; retry once with a fresh session.
    if (!retried) return apiClientFetch<T>(endpoint, options, true)
    await signIn('keycloak')
    throw new Error('Your session has expired. Please sign in again.')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      detail: response.statusText,
    }))
    throw new ApiError(normalizeApiError(errorData) || 'An API error occurred.', response.status, errorData)
  }

  // Handle responses with no content
  if (response.status === 204) {
    return null as T
  }

  return response.json() as Promise<T>
}

/**
 * Download a file from a backend endpoint using the authenticated session,
 * then trigger a browser save. Works for any `/api/...` path that returns a
 * binary body (e.g. report PDFs, invoice/sales-order PDFs); the bearer token
 * is attached just like the JSON client.
 *
 * @param endpoint A backend path including the `/api` prefix (the BASE_PATH is
 *                 expected to already be embedded by the backend when present;
 *                 we only prepend API_URL).
 * @param filename Suggested download filename.
 */
export async function downloadFile(endpoint: string, filename?: string): Promise<void> {
  const session = await currentSession()
  const headers = new Headers()
  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }
  if (session?.error === 'RefreshAccessTokenError') {
    await signIn('keycloak')
    throw new Error('Session expired. Please sign in again.')
  }

  // The backend already includes BASE_PATH in the path it hands us; only
  // prepend API_URL. Guard against a double prefix if a caller passes a
  // BASE_PATH-less path while BASE_PATH is set.
  const path = endpoint.startsWith(BASE_PATH) ? endpoint : `${BASE_PATH}${endpoint}`
  const fullUrl = `${API_URL}${path}`

  const response = await fetch(fullUrl, { method: 'GET', headers })
  if (response.status === 401) {
    await signIn('keycloak')
    throw new Error('Your session has expired. Please sign in again.')
  }
  if (!response.ok) {
    throw new Error('Failed to download file.')
  }

  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename || 'download.pdf'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  // Revoke after a tick so the click has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  /**
   * Perform a POST request.
   */
  post: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },
}

// Default export for backward compatibility
export default apiClientFetch
